import { i18n } from '@lingui/core';
import { FetchError } from 'ofetch';
import { type FC, type ReactNode, useSyncExternalStore } from 'react';
import type { ErrorOption } from 'react-hook-form';
import { SWRConfig } from 'swr';
import { parseError } from '@/base/sentry';
import { useToast } from '@/shared/Toast';

// Read the prerender-injected fallback from the page on first load. The
// prerender script appends `<script id="__swr_fallback__" type="application/json">`
// with a snapshot of every SWR cache entry it observed. Seeding `fallback`
// with this data means the first client render produces the same DOM as
// the prerendered HTML — no empty-state flash between hydration and the
// first real revalidation. Read at module load: by the time this file is
// imported (via the deferred ES module), the body has been parsed and the
// script tag is in the DOM.
const readSwrFallback = (): Record<string, unknown> => {
  if (typeof document === 'undefined') return {};
  const el = document.getElementById('__swr_fallback__');
  if (!el?.textContent) return {};
  try {
    return JSON.parse(el.textContent);
  } catch {
    return {};
  }
};
const swrFallback = readSwrFallback();

// Custom provider that publishes the cache to a global so the prerender
// script can read its contents via `page.evaluate(() => window.__APP_SWR_CACHE__)`.
// Behaves identically to SWR's default cache for everything else.
const createSwrCache = () => {
  const cache = new Map();
  if (typeof window !== 'undefined') {
    (
      window as unknown as { __APP_SWR_CACHE__: Map<string, unknown> }
    ).__APP_SWR_CACHE__ = cache;
  }
  return cache;
};

export const SwrProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const toast = useToast();
  const locale = useSyncExternalStore(
    (onChange) => i18n.on('change', onChange),
    () => i18n.locale,
    () => i18n.locale,
  );

  return (
    <SWRConfig
      value={{
        provider: createSwrCache,
        fallback: swrFallback,
        revalidateOnFocus: false,
        revalidateIfStale: true,
        keepPreviousData: true,
        shouldRetryOnError: false,
        use: [
          (hook) => {
            return (key: any, fetcher: any, config: any) => {
              if (key == null || typeof key === 'function') {
                return hook(key, fetcher, config);
              }
              const keys = Array.isArray(key)
                ? [...key, locale]
                : [key, locale];
              return hook(keys, fetcher, config);
            };
          },
        ],
        onError: (e) => {
          if (
            e instanceof FetchError &&
            [401, 403, 404].includes(e.status as never)
          )
            return;
          const parsedError = parseError(e);
          toast.add({
            title: parsedError.title,
            message: parsedError.message,
            variant: 'negative',
          });
        },
      }}
    >
      {children}
    </SWRConfig>
  );
};

export const parseApiErrors = <E extends string>(
  e?: unknown,
  setError?: (key: E, obj: ErrorOption) => void,
): Record<string, ErrorOption> => {
  const ret: Record<string, ErrorOption> = {};
  if (!(e instanceof FetchError)) return ret;
  const data = typeof e.data === 'object' && e.data ? e.data : {};
  Object.entries(data).forEach(([key, val]) => {
    const errKey =
      key === 'non_field_errors' || key === 'detail' ? 'root' : key;
    const errMsg =
      typeof val === 'string' ? [val] : Array.isArray(val) ? val : [];
    if (errMsg.length && errKey) {
      ret[errKey] = {
        message: errMsg.join('\n') as string,
      };
      setError?.(errKey as never, ret[errKey]);
    }
  });
  return ret;
};
